import { ROLE } from "~/assets/js/constants";
import { capitalize } from "~/assets/js/stringUtils";
import { isAllowedTo } from "~/assets/js/utils";
import { NavLink } from "~/types/types";
import { User } from "~/types/user/user";

/**
 * Translate function injected from the component (`this.$t`). Keeps the
 * transformer pure and unit-testable.
 */
type Translate = (key: string) => string;

export type ProductInfo = {
  imagePath: string;
  name: string;
};

export type UserInfo = {
  fullName: string;
  initials: string;
  action: boolean;
};

export type HeaderRoute = {
  id: string;
  icon: string;
  label: string;
};

export type RouteGroup = {
  id: string;
  label: string;
  routes: HeaderRoute[];
};

export type AuxiliaryAction = {
  id: string;
  icon: string;
  label: string;
  help: string;
};

export type AuxiliaryActionsContext = {
  loggedRole: ROLE;
  hasAgency: boolean;
  arePendingDownloads: boolean;
  areDownloadsCompleted: boolean;
};

/**
 * Maps the bare icon names used by the store/aside to the final CSS class.
 * Prefers the s2-icon font where a glyph exists, falls back to the Carmen
 * `mc-*` font otherwise. Extend this map as new s2 glyphs become available.
 */
const ICON_MAP: Record<string, string> = {
  tachometter: "s2-icon-gauge-light",
  building: "s2-icon-building-light",
  user: "s2-icon-user-light",
  "download-1": "s2-icon-download-light",
  cog: "s2-icon-cog-light",
  question: "s2-icon-question-light",
  "envelope-light": "s2-icon-envelope-light",
  network: "s2-icon-network-wired-light",
  // No s2-icon equivalent yet: computer, syringe, display-arrow-down, book, ticket
};

const CONFIG_ROLES = [ROLE.ADMIN];
const CLAUDIA_ROLES = [ROLE.ADMIN, ROLE.USER, ROLE.MANAGER, ROLE.SERVICEDESK, ROLE.READONLY];
const USER_CONFIG_ROLES = [ROLE.ADMIN, ROLE.USER, ROLE.MANAGER];

type ParsedNav = {
  mainRoute: HeaderRoute | null;
  groups: RouteGroup[];
};

export default class NavHeaderTransformer {
  /** Resolves a store/aside icon name to its final CSS class. */
  static toIconClass(name?: string): string {
    if (!name) return "";
    return ICON_MAP[name] ?? `mc-${name}`;
  }

  /** Logo + product name. `name` is required by the s2 validator but not displayed. */
  static toProductInfo(imagePath: string): ProductInfo {
    return { imagePath, name: "Carmen" };
  }

  /**
   * Logged user → avatar info. `action` gates whether the avatar is clickable
   * (i.e. whether `@user` is emitted to open the user configuration).
   */
  static toUserInfo(loggedUser: User | null): UserInfo {
    const firstname = loggedUser?.firstname ?? "";
    const lastname = loggedUser?.lastname ?? "";
    const fullName = `${firstname} ${lastname}`.trim();
    const initials = `${firstname.charAt(0)}${lastname.charAt(0)}`;
    const action = isAllowedTo(USER_CONFIG_ROLES, loggedUser?.role as ROLE);

    return { fullName, initials, action };
  }

  /** First nav item with a link (dashboard) → mainRoute. */
  static toMainRoute(navItems: NavLink[], t: Translate): HeaderRoute | null {
    return NavHeaderTransformer.parse(navItems, t).mainRoute;
  }

  /**
   * Remaining nav items → route groups. Divider items (textLink only, no link)
   * become group labels; the `{ sublinks: [] }` placeholder is skipped.
   */
  static toRouteGroups(navItems: NavLink[], t: Translate): RouteGroup[] {
    return NavHeaderTransformer.parse(navItems, t).groups;
  }

  /** Role/flag aware auxiliary actions (logout + user handled separately). */
  static toAuxiliaryActions(
    { loggedRole, hasAgency, arePendingDownloads }: AuxiliaryActionsContext,
    t: Translate,
  ): AuxiliaryAction[] {
    const build = (id: string, iconName: string, key: string): AuxiliaryAction => {
      const label = capitalize(t(key));
      return { id, icon: NavHeaderTransformer.toIconClass(iconName), label, help: label };
    };

    const actions: Array<{ visible: boolean; action: AuxiliaryAction }> = [
      {
        visible: arePendingDownloads,
        action: build("download-manager", "download-1", "header.actions.downloadManager"),
      },
      {
        visible: isAllowedTo(CONFIG_ROLES, loggedRole),
        action: build("config", "cog", "header.actions.config"),
      },
      {
        visible: isAllowedTo(CLAUDIA_ROLES, loggedRole),
        action: build(
          "claudia-file-downloader",
          "display-arrow-down",
          "header.actions.claudiaFileDownloader",
        ),
      },
      {
        visible: true,
        action: build("faqs", "question", "header.actions.faqs"),
      },
      {
        visible: hasAgency,
        action: build("tech-support", "envelope-light", "header.actions.techSupport"),
      },
      {
        visible: true,
        action: build("documentation", "book", "header.actions.documentation"),
      },
    ];

    return actions.filter((entry) => entry.visible).map((entry) => entry.action);
  }

  private static parse(navItems: NavLink[], t: Translate): ParsedNav {
    let mainRoute: HeaderRoute | null = null;
    const groups: RouteGroup[] = [];
    let current: RouteGroup | null = null;

    (navItems ?? []).forEach((item) => {
      // Deferred agency-tabs placeholder.
      if ("sublinks" in item) return;

      const isDivider = !item.link;
      if (isDivider) {
        current = {
          id: item.textLink ?? "",
          label: capitalize(t(item.textLink ?? "")),
          routes: [],
        };
        groups.push(current);
        return;
      }

      const route: HeaderRoute = {
        id: item.link as string,
        icon: NavHeaderTransformer.toIconClass(item.icon),
        label: capitalize(t(item.textLink ?? "")),
      };

      // The first linked item is the dashboard / mainRoute.
      if (!mainRoute) {
        mainRoute = route;
        return;
      }

      // Linked items before any divider fall into an implicit unlabelled group.
      if (!current) {
        current = { id: "__default__", label: "", routes: [] };
        groups.push(current);
      }
      current.routes.push(route);
    });

    // Drop groups with no routes (e.g. a section divider whose links were all
    // filtered out by authorization) so s2 does not render an empty header.
    return { mainRoute, groups: groups.filter((group) => group.routes.length > 0) };
  }
}
